import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { MFE_DIALOG_PREFIX } from '@/features/app/dialogs/dialogConstants';
import { type ColumnKey, readColumnSettings, writeColumnSettings } from '../columnSettings';

export const SHOW_WORKSHOP_FILTER_PARAM = 'show_workshop_filter';

const shouldShowWorkshopFilter = (): boolean =>
    new URLSearchParams(window.location.search).get(SHOW_WORKSHOP_FILTER_PARAM) === 'true';

const OPTIONAL_COLUMNS: { key: ColumnKey; messageId: string }[] = [
    { key: 'vin', messageId: 'intl-msg:engineOil.table.vin' },
    { key: 'workshop', messageId: 'intl-msg:engineOil.table.workshop' },
    { key: 'customer', messageId: 'intl-msg:engineOil.table.customer' },
];

const getVisibleColumns = () =>
    OPTIONAL_COLUMNS.filter((column) => column.key !== 'workshop' || shouldShowWorkshopFilter());

const TableSettingsDialog = () => {
    const { sendMessageToParent } = usePostMessageSender();
    const [columnSettings, setColumnSettings] = useState(readColumnSettings);
    const visibleColumns = getVisibleColumns();

    const handleCloseDialog = () => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${CLOSE_DIALOG_EVENT}`,
            payload: 'Closed table settings dialog',
        });
    };

    const handleToggleColumn = (key: ColumnKey) => {
        setColumnSettings((current) => ({ ...current, [key]: !current[key] }));
    };

    const handleSave = () => {
        writeColumnSettings(columnSettings);
        handleCloseDialog();
    };

    return (
        <div className="bg-white min-height-200">
            <Dialog.Title
                title={<FormattedMessage id="intl-msg:engineOil.table.settings.title" />}
                onCloseButtonClick={handleCloseDialog}
            />

            <Dialog.Body className="padding-20">
                <div>
                    <strong>
                        <FormattedMessage id="intl-msg:engineOil.table.settings.optionalColumns" />
                    </strong>
                </div>
                <div className="display-flex flex-column gap-10 margin-top-10">
                    {visibleColumns.map((column) => (
                        <Checkbox
                            key={column.key}
                            checked={columnSettings[column.key]}
                            onChange={() => handleToggleColumn(column.key)}
                        >
                            <FormattedMessage id={column.messageId} />
                        </Checkbox>
                    ))}
                </div>
            </Dialog.Body>

            <Dialog.Footer>
                <div className="display-flex justify-content-end gap-10">
                    <button className="btn btn-default" onClick={handleCloseDialog}>
                        <FormattedMessage id="intl-msg:engineOil.table.settings.close" />
                    </button>
                    <button className="btn btn-primary" onClick={handleSave}>
                        <FormattedMessage id="intl-msg:engineOil.table.settings.save" />
                    </button>
                </div>
            </Dialog.Footer>
        </div>
    );
};

export default TableSettingsDialog;
